// Card 22, the speed label (R548, ui-map-interface.md §17.3.22).
//
// The owner cut card 22 down to the label alone: the dart shapes stay as the
// fleet layer draws them, and this piece adds the speed at the bottom right
// of the selected or the hovered own hull. No course figure, no leader and
// no heading.
//
// **The selected or the hovered own hull only** (R1445). A speed over a
// contact is a course the server withheld, which is the rule of §5. A
// label over every own hull was noise on a map with many hulls, so the
// owner cut it to the hull the commander selected and the hull under the
// pointer.
//
// The figure is the speed in px per WORLD MINUTE (R591, and R559 before
// it). `ChartFleet.speed` is the rendered speed in px per wall second, so
// one world minute of travel is `speed * 60 / timescale` px. Every
// duration the client shows reads in world time, and this label is the
// distance side of the same rule: it states what a player sees the hull do
// against the world clock. The unit is therefore NOT the px per map hour
// of the ship table (`numbers.md §3`, `speed_uh`), which is the designer's
// figure and stands still at every timescale.
//
// The first build stated px per map second, and every hull in the game is
// under one px a second, so the label rounded to 0 and the floor hid it.
//
// **The figure stands still between two readings** (R587). `f.speed` is a
// smoothed speed, and it still moves by units on every frame, so a label
// written from it flickered. The piece keeps one held figure for each own
// fleet: it reads the speed once every `hold_sec` wall seconds, rounds it to
// `round_to` px per world minute, and writes the held figure until the
// hold runs out. The `min_speed` floor reads the held figure too, so the
// label does not blink in and out at the floor either.

import { chart as S } from '../../../gen/strings';
import type { FrameInfo } from '../../layer';
import { CHART_INK, faceOf, offCull } from './shared';
import { isNamedFleet } from './beacon-read';
import type {
  ChartContext, ChartSinks, Piece,
} from '../context';

export class SpeedLabelPiece implements Piece {
  readonly id = 'speed_label' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = [] as const;

  /** The figure that stands for one own fleet, and the time it was read. */
  private readonly held = new Map<number, { px: number; at: number }>();

  rebuild(_ctx: ChartContext): void { this.held.clear(); }

  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void {
    const fleets = ctx.scene.fleets;
    if (!fleets) return;
    const t = ctx.cfg.pieces.speed_label;
    const fig = t.fig;
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const scale = ctx.state.timescale || 1;
    const cull = ctx.cull;
    const now = frame.timeS;
    // A fleet that is still drawn was read within one hold, so an entry
    // that is two holds old belongs to a fleet that is gone.
    const stale = now - 2 * fig.hold_sec;
    for (const [id, entry] of this.held) {
      if (entry.at < stale) this.held.delete(id);
    }
    for (const f of fleets.chartFleets()) {
      if (!f.own || !isNamedFleet(ctx, f.id)) continue;
      if (offCull(cull, f.x, f.y)) continue;
      // A fleet takes one entry, and every later reading is written into
      // it, so a reading allocates nothing.
      let entry = this.held.get(f.id);
      if (!entry) {
        entry = { px: 0, at: -Infinity };
        this.held.set(f.id, entry);
      }
      if (now - entry.at >= fig.hold_sec) {
        const raw = (f.speed * 60) / scale;
        entry.px = Math.round(raw / fig.round_to) * fig.round_to;
        entry.at = now;
      }
      const px = entry.px;
      // A hull at rest states nothing. A label that reads `0 px/min` over
      // every anchored Beacon is noise, not information.
      if (px < fig.min_speed) continue;
      const text = hist ? S.speed_historical({ px }) : S.speed_modern({ px });
      ctx.glyphs.word(
        sinks.quads, text, f.x + fig.dx, f.y + fig.dy,
        face, t.text.main, CHART_INK.pale, fig.alpha,
      );
    }
  }
}

export const speedLabel = new SpeedLabelPiece();
